/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * The reducer for table component
 *
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Misha Kokoulin
 * @author Maxim Fedorov
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import produce from 'immer';
import get from 'lodash/get';
import type { Reducer } from 'redux';

import { IWizardImportSubscribersLimitsTableData } from '@/main/pages/WizardImportSubscribersLimits/types/wizardImportSubscribersLimitsTypes';
import { EImportSubscribersLimitsField } from '@/main/pages/WizardImportSubscribersLimits/wizardImportSubscribersLimitsConstant';
import { PersonalAccountField } from '@/main/types/personalAccountTypes';
import type { IPersonalAccount } from '@/main/types/personalAccountTypes';
import { SubscriberMobileField } from '@/main/types/subscriberTypes';
import type { ISubscriberMobile } from '@/main/types/subscriberTypes';
import type { IInquiryState } from '@modules/inquiryState/inquiryStateTypes';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { LOGOUT_START_SUCCESSFUL } from '@pages/PageAuth/constants/authConstants';
import { EAutoReportIdTableAccountField, EAutoReportIdTableSubscriberField } from '@pages/PageAutoReportId/components/Table/AutoReportIdTableCell';
import type { TAutoReportIdTableAccountData, TAutoReportIdTableSubscriberData } from '@pages/PageAutoReportId/components/Table/AutoReportIdTableCell';
import { BillsField } from '@pages/PageBills/pageBillsTypes';
import type { IBills } from '@pages/PageBills/pageBillsTypes';
import {
    ECampaignInfoTableAccountField,
    ECampaignInfoTableSubscriberField,
} from '@pages/PageCampaignInfo/components/Table/CampaignInfoTableCells';
import type {
    TCampaignInfoTableAccountData,
    TCampaignInfoTableSubscriberData,
} from '@pages/PageCampaignInfo/components/Table/CampaignInfoTableCells';
import { CashbackHistoryTableField } from '@pages/PageCashback/types/cashbackHistoryTypes';
import type { ICashbackHistoryTableData } from '@pages/PageCashback/types/cashbackHistoryTypes';
import {
    EExpensesAnalyzerField,
} from '@pages/PageExpensesAnalyzer/components/ExpensesAnalyzerTable/types/expensesAnalyzerTableTypes';
import type {
    IExpensesAnalyzerData,
} from '@pages/PageExpensesAnalyzer/components/ExpensesAnalyzerTable/types/expensesAnalyzerTableTypes';
import { ELabelsImportField } from '@pages/PageLabelsImport/labelsImportConstants';
import type { ILabelsImportTableData } from '@pages/PageLabelsImport/labelsImportTypes';
import { EPagePaymentsTableField } from '@pages/PagePayments/const/PagePaymentsTable';
import type { TPagePaymentsTableData } from '@pages/PagePayments/const/PagePaymentsTable';
import {
    ESettingsAutoReportsField,
} from '@pages/PageSettings/components/SettingsAutoReports/components/SettingsAutoReportsTable/settingsAutoReportsTableTypes';
import type {
    ISettingsAutoReportsTableData,
} from '@pages/PageSettings/components/SettingsAutoReports/components/SettingsAutoReportsTable/settingsAutoReportsTableTypes';
import {
    ESettingsStatisticsField,
} from '@pages/PageSettings/components/SettingsStatistics/components/SettingsStatisticsTable/settingsStatisticsTableTypes';
import type {
    ISettingsStatisticsTableData,
} from '@pages/PageSettings/components/SettingsStatistics/components/SettingsStatisticsTable/settingsStatisticsTableTypes';
import type { IWizardImportPersonalDataTableData } from '@pages/WizardImportPersonalData/types/wizardImportPersonalDataTypes';
import { EImportPersonalDataField } from '@pages/WizardImportPersonalData/wizardImportPersonalDataConstant';
import { EProductSubscribersTableField } from '@pages/WizardProductOfferings/wizardProductOfferingsConstants';
import type { IWizardProductOfferingsTableData } from '@pages/WizardProductOfferings/wizardProductOfferingsTypes';

import type { IOptionItemProps } from '../../common/Select/Select';
import type { TableActionsType } from '../actions/tableActions';
import type { IFilterSort, IOnFilterChangeParams } from '../components/Filters/Filter';
import { TABLE_LOAD_SIZE } from '../constants';
import {
    ADD_TABLE_SUB_ROW_ID,
    CHANGE_TABLE_MODAL_VALUE,
    COLLAPSE_TABLE_SUB_ROWS,
    DROP_TABLE_FILTER_AND_SORT,
    GET_BASKET_COUNT_SUCCESSFUL,
    LOAD_TABLE_FIELDS_ERROR,
    LOAD_TABLE_FIELDS_SUCCESSFUL,
    LOAD_TABLE_LIST,
    LOAD_TABLE_LIST_ERROR,
    LOAD_TABLE_LIST_FULLY,
    LOAD_TABLE_LIST_SUCCESSFUL,
    LOAD_TABLE_META,
    LOAD_TABLE_META_ERROR,
    LOAD_TABLE_META_PARAMETERS_SUCCESSFUL,
    LOAD_TABLE_META_SUCCESSFUL,
    LOAD_TABLE_SUB_ROW_ERROR,
    MOBILE_CASH_FILE_LOADING,
    MOBILE_CASH_FILE_SUCCESSFULL,
    RESET_BASKET_COUNT,
    SEND_FIELD_SUGGEST,
    SEND_FIELD_SUGGEST_FAILED,
    SEND_FIELD_SUGGEST_SUCCESSFUL,
    SEND_TABLE_FILTER_AND_SORT_ERROR,
    SEND_TABLE_SEARCH,
    SEND_TABLE_SEARCH_ERROR,
    SEND_TABLE_SEARCH_SUCCESSFUL,
    SET_BASKET_CONTEXT,
    SET_BASKET_PARAMS,
    SET_BASKET_VALID,
    SET_IS_FAST_ACTION_PAGE,
    SET_IS_FAST_BASKET_PAGE,
    SET_TABLE_CURRENT_NAME,
    SET_TABLE_FILTER,
    SET_TABLE_IS_LOADING,
    SET_TABLE_MAIN_CHECKBOX,
    SET_TABLE_SORT,
    SET_TABLE_SUB_ROW_DATA,
    START_TICK,
    STOP_TICK,
    SWITCH_CHECKBOX_LOADING,
    SWITCH_CHECKBOX_SUCCESSFUL,
    TABLE_BUTTONS_SET_MODE,
    TABLE_REMOVE_ROW_FAILURE,
    TABLE_REMOVE_ROW_SUCCESS,
    TABLE_RESET,
    TABLE_ROW_DELETE_ACTION,
    TABLE_ROW_RESTORE_ACTION,
    TABLE_ROW_RESTORE_ALL_ACTION,
    UPDATE_ROWS_START,
    UPDATE_ROWS_START_FAILURE,
    UPDATE_ROWS_START_SUCCESSFUL,
} from '../constants/actionConstants';
import type { ColumnFieldType } from '../types/columnTypes';
import type { ITableFilterSearch } from '../types/tableFilterTypes';
import type { ITableButtonTypes } from '../types/tableFooterButtonsTypes';
import { ETableButtonsMode, TableNameType } from '../types/tableTypes';
import type {
    IChangeTableModalValuePayload,
    TableDataType,
    TableFooterType,
} from '../types/tableTypes';

export type TableFieldType = 'C' | 'M' | 'S';

/**
 * @description Describe table fields
 * @interface ITableField
 */
export interface ITableField<Field> {
    checked: boolean;
    description?: string;
    label: string;
    name: Field;
    order: number;
    resizable: boolean;
    size: number;
    type: TableFieldType;
}

export interface ITableState<Field extends ColumnFieldType, DataType extends TableDataType> {
    basketAllIn: boolean;
    fastActionAllIn?: boolean;
    basketCount: number | null;
    basketValid: boolean;
    basketParams?: ITableBasketParams;
    buttons: ITableButtonTypes[];
    buttonsMode: ETableButtonsMode | null;
    buttonsForCheckOnMode: AccessComponentKeyEnum[];
    count: number;
    collapseTableSubRows: boolean;
    data: DataType[];
    exportExtensions: string[];
    removedRowIndexes: number[];
    fields: Array<ITableField<Field>>;
    filter: { [Key in Field]?: IOnFilterChangeParams };
    filterOptions: { [Key in Field]?: IOptionItemProps[] };
    filterSearch: ITableFilterSearch;
    isLoaded: boolean;
    isLoading: boolean;
    message: string;
    metaFacets: Field[];
    page: number;
    parameters?: ITableMetaParameters;
    sort: IFilterSort;
    subRowsIds: number[];
    suggestLoading: boolean;
    tableFooter?: TableFooterType;
    searchAfterValues?: string[] | null;
    isMobileCashFileLoading?: boolean;
    isMobileCashFileSuccessful?: boolean;
}

interface ITableMetaParameters {
    loadState?: IInquiryState;

    [key: string]: any;
}

export interface ITableBasketParams {
    valid?: {
        [Button in AccessComponentKeyEnum]?: boolean;
    };

    [key: string]: any;
}

export interface ITableReducer {
    [TableNameType.account]: ITableState<PersonalAccountField, IPersonalAccount>;
    [TableNameType.autoReportIdAccount]: ITableState<EAutoReportIdTableAccountField, TAutoReportIdTableAccountData>;
    [TableNameType.autoReportIdSubscriber]: ITableState<
        EAutoReportIdTableSubscriberField,
        TAutoReportIdTableSubscriberData
    >;
    [TableNameType.autoReportIdAccountEdit]: ITableState<PersonalAccountField, IPersonalAccount>;
    [TableNameType.autoReportIdSubscriberEdit]: ITableState<SubscriberMobileField, ISubscriberMobile>;
    [TableNameType.bills]: ITableState<BillsField, IBills>;
    [TableNameType.campaignInfoAccount]: ITableState<ECampaignInfoTableAccountField, TCampaignInfoTableAccountData>;
    [TableNameType.campaignInfoSubscriber]: ITableState<
        ECampaignInfoTableSubscriberField,
        TCampaignInfoTableSubscriberData
    >;
    [TableNameType.cashbackHistory]: ITableState<CashbackHistoryTableField, ICashbackHistoryTableData>;
    [TableNameType.expensesAnalyzer]: ITableState<EExpensesAnalyzerField, IExpensesAnalyzerData>;
    [TableNameType.importPersonalData]: ITableState<EImportPersonalDataField, IWizardImportPersonalDataTableData>;
    [TableNameType.importSubscribersLimits]: ITableState<
        EImportSubscribersLimitsField, 
        IWizardImportSubscribersLimitsTableData
    >;
    [TableNameType.labelsImport]: ITableState<ELabelsImportField, ILabelsImportTableData>;
    [TableNameType.payments]: ITableState<EPagePaymentsTableField, TPagePaymentsTableData>;
    [TableNameType.productSelectSubscribers]: ITableState<
        EProductSubscribersTableField,
        IWizardProductOfferingsTableData
    >;
    [TableNameType.settingsAutoReports]: ITableState<ESettingsAutoReportsField, ISettingsAutoReportsTableData>;
    [TableNameType.settingsStatistics]: ITableState<ESettingsStatisticsField, ISettingsStatisticsTableData>;
    [TableNameType.wizardBillDocAccount]: ITableState<PersonalAccountField, IPersonalAccount>;
    currentTableName: TableNameType;
    modalProps: IChangeTableModalValuePayload;
    basketContext?: string;
    isFastActionPage?: boolean;
    isFastBasketPage?: boolean;
}

export const initTableState = {
    basketAllIn: false,
    fastActionAllIn: false,
    basketCount: null,
    basketValid: true,
    buttons: [],
    buttonsMode: null,
    buttonsForCheckOnMode: [],
    collapseTableSubRows: false,
    count: 0,
    data: [],
    exportExtensions: [],
    removedRowIndexes: [],
    fields: [],
    filter: {},
    filterOptions: {},
    filterSearch: {
        searchValue: '',
        searchResult: [],
    },
    isLoaded: false,
    isLoading: false,
    message: '',
    metaFacets: [],
    page: 0,
    sort: {},
    subRowsIds: [],
    suggestLoading: false,
};

export const initialTableState: ITableReducer = {
    [TableNameType.account]: initTableState,
    [TableNameType.autoReportIdAccount]: initTableState,
    [TableNameType.autoReportIdSubscriber]: initTableState,
    [TableNameType.autoReportIdAccountEdit]: initTableState,
    [TableNameType.autoReportIdSubscriberEdit]: initTableState,
    [TableNameType.bills]: initTableState,
    [TableNameType.campaignInfoAccount]: initTableState,
    [TableNameType.campaignInfoSubscriber]: initTableState,
    [TableNameType.cashbackHistory]: initTableState,
    [TableNameType.expensesAnalyzer]: initTableState,
    [TableNameType.importPersonalData]: initTableState,
    [TableNameType.importSubscribersLimits]: initTableState,
    [TableNameType.labelsImport]: initTableState,
    [TableNameType.payments]: initTableState,
    [TableNameType.productSelectSubscribers]: initTableState,
    [TableNameType.settingsAutoReports]: initTableState,
    [TableNameType.settingsStatistics]: initTableState,
    [TableNameType.wizardBillDocAccount]: initTableState,
    currentTableName: TableNameType.account,
    modalProps: {},
};

const tableReducer: Reducer<ITableReducer, TableActionsType> = (
    state: ITableReducer = initialTableState,
    action: TableActionsType,
) =>
    produce(state, (draft: ITableReducer) => {
        draft.currentTableName = get(action, 'payload.tableName', draft.currentTableName);
        const currentTable = draft[draft.currentTableName];
        let tableName: TableNameType;

        switch (action.type) {
            case SET_BASKET_CONTEXT:
                draft.basketContext = action.payload;
                break;
            case SET_IS_FAST_ACTION_PAGE:
                draft.isFastActionPage = action.payload;
                break;
            case SET_IS_FAST_BASKET_PAGE:
                draft.isFastBasketPage = action.payload;
                break;

            case SET_TABLE_CURRENT_NAME:
                draft.currentTableName = action.payload;
                break;

            case MOBILE_CASH_FILE_LOADING:
                currentTable.isMobileCashFileLoading = action.payload;
                break;
            case MOBILE_CASH_FILE_SUCCESSFULL:
                currentTable.isMobileCashFileSuccessful = action.payload;
                break;
            case LOAD_TABLE_META:
                currentTable.isLoading = true;
                currentTable.subRowsIds = [];
                currentTable.basketParams = undefined;
                currentTable.removedRowIndexes = [];
                break;
            case LOAD_TABLE_META_SUCCESSFUL:
                currentTable.buttons = action.payload.buttons;
                currentTable.exportExtensions = action.payload.exportExtensions;
                currentTable.fields = action.payload.fields;
                currentTable.filter = action.payload.filter;
                currentTable.filterOptions = action.payload.filterOptions;
                currentTable.metaFacets = action.payload.metaFacets;
                currentTable.parameters = action.payload.parameters;
                currentTable.sort.direction = action.payload.sort.direction;
                currentTable.sort.property = action.payload.sort.property;
                break;
            case LOAD_TABLE_META_PARAMETERS_SUCCESSFUL:
                currentTable.parameters = { ...currentTable.parameters, ...action.payload };
                break;
            case LOAD_TABLE_META_ERROR:
                currentTable.isLoading = false;
                currentTable.message = action.payload.error;
                break;

            case LOAD_TABLE_LIST:
                currentTable.isLoading = true;
                if (action.payload?.force) currentTable.data = initTableState.data;
                break;
            case LOAD_TABLE_LIST_FULLY:
                currentTable.isLoading = false;
                break;
            case LOAD_TABLE_LIST_SUCCESSFUL:
                currentTable.basketAllIn = action.payload.basketAllIn;
                currentTable.count = action.payload.count;
                currentTable.data = action.payload.data;
                currentTable.isLoaded = true;
                currentTable.isLoading = false;
                currentTable.page = Math.ceil(currentTable.data.length / TABLE_LOAD_SIZE);
                currentTable.tableFooter = action.payload.tableFooter;
                currentTable.searchAfterValues = action.payload.searchAfterValues;

                if (action.payload.metaFacets) {
                    currentTable.metaFacets = action.payload.metaFacets;
                }

                if (
                    action.payload.count === 1 &&
                    draft.currentTableName === TableNameType.account &&
                    typeof action.payload.fastActionAllIn === 'undefined'
                ) {
                    currentTable.data[0] = { ...action.payload.data[0] as IPersonalAccount, fastActionChecked: true }
                    currentTable.fastActionAllIn = true;
                } else {
                    currentTable.fastActionAllIn = action.payload.fastActionAllIn;
                }

                break;
            case LOAD_TABLE_LIST_ERROR:
                currentTable.isLoading = false;
                currentTable.message = action.payload.error;
                break;
            case ADD_TABLE_SUB_ROW_ID:
                currentTable.subRowsIds = [...currentTable.subRowsIds, action.payload.subRowId];
                break;
            case COLLAPSE_TABLE_SUB_ROWS:
                currentTable.collapseTableSubRows = action.payload;
                break;
            case SET_TABLE_SUB_ROW_DATA:
                currentTable.data = action.payload.data;
                break;
            case LOAD_TABLE_SUB_ROW_ERROR:
                currentTable.message = action.payload.error;
                currentTable.subRowsIds = currentTable.subRowsIds.filter(
                    (subRowId) => subRowId !== action.payload.subRowId,
                );
                break;
            case LOAD_TABLE_FIELDS_SUCCESSFUL:
                currentTable.fields = action.payload.fields;
                break;
            case LOAD_TABLE_FIELDS_ERROR:
                currentTable.message = action.payload.error;
                break;

            case SET_TABLE_FILTER:
                currentTable.filter = action.payload.filter;
                currentTable.isLoading = true;
                currentTable.subRowsIds = [];
                break;
            case SET_TABLE_SORT:
                currentTable.isLoading = true;
                currentTable.sort.direction = action.payload.order;
                currentTable.sort.property = action.payload.property;
                currentTable.subRowsIds = [];
                break;
            case SEND_TABLE_FILTER_AND_SORT_ERROR:
                currentTable.isLoading = false;
                currentTable.message = action.payload.error;
                break;

            case SWITCH_CHECKBOX_LOADING:
                currentTable.isLoading = true;
                break;

            case SWITCH_CHECKBOX_SUCCESSFUL:
                currentTable.isLoading = false;
                break;

            case SET_TABLE_MAIN_CHECKBOX:
                currentTable.basketAllIn = action.payload.basketAllIn;
                break;
            case GET_BASKET_COUNT_SUCCESSFUL:
                currentTable.basketCount = action.payload;
                break;
            case SET_BASKET_VALID:
                currentTable.basketValid = action.payload;
                break;
            case SET_BASKET_PARAMS:
                currentTable.basketParams = action.payload;
                break;

            case DROP_TABLE_FILTER_AND_SORT:
                currentTable.data = initialTableState[state.currentTableName].data;
                currentTable.filter = {};
                currentTable.filterSearch.searchResult = [];
                currentTable.filterSearch.searchValue = '';
                currentTable.isLoading = true;
                break;

            case SEND_TABLE_SEARCH:
                currentTable.filterSearch.searchResult = [];
                currentTable.filterSearch.searchValue = action.payload.search;
                break;
            case SEND_TABLE_SEARCH_SUCCESSFUL:
                currentTable.filterSearch.searchResult = action.payload.result;
                break;
            case SEND_TABLE_SEARCH_ERROR:
                currentTable.filterSearch.searchResult = [];
                currentTable.filterSearch.searchValue = '';
                currentTable.message = action.payload.error;
                break;

            case SEND_FIELD_SUGGEST:
                currentTable.suggestLoading = true;
                break;
            case SEND_FIELD_SUGGEST_SUCCESSFUL:
                currentTable.filterOptions = action.payload;
                currentTable.suggestLoading = false;
                break;
            case SEND_FIELD_SUGGEST_FAILED:
                currentTable.message = action.payload;
                currentTable.suggestLoading = false;
                break;

            case TABLE_BUTTONS_SET_MODE:
                currentTable.buttonsMode = action.payload.buttonsMode;
                currentTable.buttonsForCheckOnMode = action.payload.buttonsForCheckOnMode;
                break;

            case CHANGE_TABLE_MODAL_VALUE:
                draft.modalProps = action.payload;
                break;

            case LOGOUT_START_SUCCESSFUL:
                return initialTableState;

            case UPDATE_ROWS_START:
                break;
            case UPDATE_ROWS_START_SUCCESSFUL:
                break;
            case UPDATE_ROWS_START_FAILURE:
                break;

            case START_TICK:
                break;
            case STOP_TICK:
                break;

            case RESET_BASKET_COUNT:
                tableName = action.payload?.tableName ?? draft.currentTableName;
                draft[tableName].basketCount = initTableState.basketCount;
                break;
            case SET_TABLE_IS_LOADING:
                tableName = action.payload.tableName ?? draft.currentTableName;
                draft[tableName].isLoading = action.payload.isLoading;
                break;
            case TABLE_REMOVE_ROW_SUCCESS:
                tableName = action.payload.tableName ?? draft.currentTableName;
                const { removeId, selectRowIdCallback } = action.payload;
                const updatedData = (draft[tableName].data as TableDataType[]).filter(
                    (row) => selectRowIdCallback(row) !== removeId,
                );
                if (updatedData.length < draft[tableName].data.length) {
                    draft[tableName].count -= 1;
                }
                (draft[tableName].data as TableDataType[]) = updatedData;
                break;
            case TABLE_REMOVE_ROW_FAILURE:
                currentTable.message = action.payload.errorMessage;
                break;
            case TABLE_RESET:
                tableName = action.payload?.tableName ?? draft.currentTableName;
                draft[tableName] = initTableState;
                break;

            /*
             * -------------------------------------------
             *        DELETE ROW IN TABLE
             * -------------------------------------------
             */
            case TABLE_ROW_DELETE_ACTION:
                tableName = action.payload?.tableName ?? draft.currentTableName;
                draft[tableName].removedRowIndexes.push(action.payload?.index);
                break;
            case TABLE_ROW_RESTORE_ACTION:
                tableName = action.payload?.tableName ?? draft.currentTableName;
                draft[tableName].removedRowIndexes.splice(
                    draft[tableName].removedRowIndexes.indexOf(action.payload?.index),
                    1,
                );
                break;
            case TABLE_ROW_RESTORE_ALL_ACTION:
                tableName = action.payload?.tableName ?? draft.currentTableName;
                draft[tableName].removedRowIndexes = [];
                break;

            default:
                break;
        }
    });
export default tableReducer;
